import { useCallback, useRef, useState } from 'react';
import { Upload, X, FileImage, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { uploadReceipt, setUploadProgress } from '@/features/receipts/receiptsSlice';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import ReceiptCategorySuggestion from './ReceiptCategorySuggestion';
import api from '@/services/api';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];

interface ReceiptUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploaded?: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ReceiptUploadDialog({ open, onOpenChange, onUploaded }: ReceiptUploadDialogProps) {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { uploading, uploadProgress } = useAppSelector((s) => s.receipts);

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<{
    receiptId: string;
    originalSize: number;
    compressedSize: number;
    suggestion?: { category: string; confidence: string; reason: string };
  } | null>(null);
  const [categorizing, setCategorizing] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = useCallback(() => {
    setFile(null);
    setPreview(null);
    setNotes('');
    setTagsInput('');
    setValidationError(null);
    setUploadResult(null);
    dispatch(setUploadProgress(0));
  }, [dispatch]);

  const validateFile = useCallback((f: File): string | null => {
    if (!ACCEPTED_TYPES.includes(f.type)) {
      return 'Invalid file type. Accepted: JPEG, PNG, WebP, HEIC, PDF.';
    }
    if (f.size > MAX_FILE_SIZE) {
      return `File too large (${formatBytes(f.size)}). Max size: 10 MB.`;
    }
    return null;
  }, []);

  const handleFileSelect = useCallback((f: File) => {
    const err = validateFile(f);
    if (err) {
      setValidationError(err);
      setFile(null);
      setPreview(null);
      return;
    }
    setValidationError(null);
    setFile(f);
    setUploadResult(null);

    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target?.result as string);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  }, [validateFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleUpload = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    if (notes.trim()) formData.append('notes', notes.trim());
    if (tagsInput.trim()) {
      const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean);
      tags.forEach((t) => formData.append('tags', t));
    }

    try {
      const result = await dispatch(
        uploadReceipt({
          formData,
          onProgress: (pct) => dispatch(setUploadProgress(pct)),
        })
      ).unwrap();

      setUploadResult({
        receiptId: result.data._id,
        originalSize: result.data.originalSize,
        compressedSize: result.data.compressedSize,
        suggestion: result.suggestion,
      });

      toast({ title: 'Receipt uploaded', description: result.data.originalFilename });
      onUploaded?.();
    } catch (err: unknown) {
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  const handleClose = (val: boolean) => {
    if (!val) resetForm();
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="receipt-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload Receipt</DialogTitle>
          <DialogDescription>Upload a receipt image or PDF (max 10 MB)</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Drop zone */}
          {!uploadResult && (
            <div
              data-testid="receipt-drop-zone"
              className={cn(
                'relative flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors',
                dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-muted-foreground/50',
                file && 'border-green-500/50 bg-green-50/50 dark:bg-green-950/20',
              )}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES.join(',')}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileSelect(f);
                }}
                data-testid="receipt-file-input"
              />

              {preview ? (
                <div className="relative">
                  <img src={preview} alt="Preview" className="max-h-40 rounded-md object-contain" />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute -right-2 -top-2 h-6 w-6"
                    onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : file ? (
                <div className="flex items-center gap-2">
                  <FileImage className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium break-words">{file.name}</p>
                    <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Drag & drop or <span className="font-medium text-primary">browse</span>
                  </p>
                  <p className="text-xs text-muted-foreground">JPEG, PNG, WebP, HEIC, PDF • Max 10 MB</p>
                </>
              )}
            </div>
          )}

          {validationError && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <p>{validationError}</p>
            </div>
          )}

          {/* Upload progress */}
          {uploading && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} />
            </div>
          )}

          {/* Upload result — compression info */}
          {uploadResult && (
            <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                Upload successful
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-muted-foreground">Original</p>
                  <p className="font-medium">{formatBytes(uploadResult.originalSize)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Compressed</p>
                  <p className="font-medium">{formatBytes(uploadResult.compressedSize)}</p>
                </div>
              </div>
              {uploadResult.suggestion && (
                <ReceiptCategorySuggestion
                  suggestion={{
                    category: uploadResult.suggestion.category,
                    confidence: uploadResult.suggestion.confidence as 'high' | 'medium' | 'low',
                    reason: uploadResult.suggestion.reason,
                  }}
                  onApply={async (category) => {
                    setCategorizing(true);
                    try {
                      await api.post(`/receipts/${uploadResult.receiptId}/categorize`, { category });
                      toast({ title: 'Category applied' });
                      setUploadResult((prev) => prev ? { ...prev, suggestion: undefined } : null);
                    } catch {
                      toast({ title: 'Failed to apply category', variant: 'destructive' });
                    }
                    setCategorizing(false);
                  }}
                  applying={categorizing}
                />
              )}
            </div>
          )}

          {/* Notes & Tags (pre-upload) */}
          {!uploadResult && (
            <div className="space-y-3">
              <div>
                <label htmlFor="receipt-notes" className="text-sm font-medium">
                  Notes <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <Textarea
                  id="receipt-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes..."
                  className="mt-1"
                  rows={2}
                  data-testid="receipt-notes"
                />
              </div>
              <div>
                <label htmlFor="receipt-tags" className="text-sm font-medium">
                  Tags <span className="text-muted-foreground font-normal">(comma-separated)</span>
                </label>
                <Input
                  id="receipt-tags"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="e.g., groceries, tax-deductible"
                  className="mt-1"
                  data-testid="receipt-tags"
                />
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          {uploadResult ? (
            <Button onClick={() => handleClose(false)} data-testid="receipt-upload-done">
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleClose(false)} disabled={uploading}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={!file || uploading} data-testid="receipt-upload-submit">
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
